/**
 * The three.js side of combat: every other aeroplane in the sky, the balloons
 * and airships on the ground, and the rounds in flight. Rounds are pooled —
 * three `InstancedMesh`es, one per bullet size bucket — so a dogfight
 * allocates nothing per frame and adds three draw calls, not two hundred.
 *
 * Smoke, explosions and every other screen-space sprite moved to
 * `sprite-effects.ts` when the `.TBL` sprite nodes were brought in-world: the
 * damage trail is now `SMOKE.BMP`'s own puff art rather than a `PointsMaterial`
 * dot, and it shares its pool and its camera basis with the fireballs.
 *
 * Aeroplanes are pooled a level up, by *model*: a mission puts four different
 * airframes in the sky, and each `.TBL` is fetched and turned into geometry
 * exactly once however many aeroplanes of that type are flying (`meshCache`).
 * A slot is a `Group` per live participant, so an aeroplane appearing or being
 * destroyed costs a visibility flag rather than a reload.
 *
 * Gunfire is **pixel projectiles only** (user directive, 2026-08-19): the
 * original's own `BULLETS.BMP` rounds streaming out of the breeches along the
 * engine's boresight-ballistic paths, with no line geometry anywhere. The
 * point is to read the fight, not to light it up.
 *
 * A round's quad is camera-facing *and* rolled onto the round's apparent
 * bearing, because a `BULLETS.BMP` in-flight frame is a 16x4 streak and a
 * streak has to lie along where the thing is going. Billboarding it off the
 * camera's axes alone laid every round down as a horizontal dash, which is the
 * "aligned sideways" of the 2026-08-19 report; `bullet-sprites.ts`'s
 * `bulletStreak` owns that geometry and this file only consumes it.
 *
 * The roll is the *only* thing done to the quad's shape here. Its width and
 * height arrive already in the frame's own aspect (`fillBulletPool`), and the
 * scale written into the instance matrix must keep them that way: scaling one
 * axis against the other squashes the streak inside it, which is the
 * 2026-08-21 "much less big … some distorted sprite" report.
 *
 * ## A round's material is opaque, and that is load-bearing (2026-08-22)
 *
 * `bulletTracerMaterial` has the measurement: under this renderer's reversed-Z
 * depth buffer a bullet mesh with `transparent: true` or `depthWrite: false`
 * draws **nothing at all** — not a dim mark, zero pixels — which is what "I
 * cannot see the bullets in any view" was. Nothing about the geometry was ever
 * wrong: the same instance matrices in the same frames rasterize correctly the
 * moment the material stops opting out of the depth buffer.
 *
 * ## ENHANCED EFFECTS swaps the whole gunfire path (2026-08-23)
 *
 * With the toggle on, a round is drawn by `three/tracer-pool.ts` instead — a
 * hot head with a glowing tail along the round's world velocity — and the three
 * `BULLETS.BMP` meshes are told to draw nothing. **Replace, never add**, the
 * same rule the toggle already enforces for aeroplane fire, smoke and the train
 * plume, and `writeBullets` below is the single place the two paths are chosen
 * between so nothing can end up carrying both.
 *
 * Off, every pixel of the gunfire is exactly what it was before the tracer pool
 * existed: `fillBulletPool` is called with the same arguments in the same
 * order, on the same rounds, and nothing in `bullet-sprites.ts` was touched.
 * `apps/web/test/tracer-pool.test.ts` pins that.
 *
 * Bombs are the one thing that is *not* swapped. `roundViews` puts them in the
 * same list on purpose, and a falling bomb is a lump of iron rather than an
 * incandescent round, so it stays on the pixel-projectile path in both states —
 * which is why the bullet meshes still exist and still get a fill when the
 * toggle is on, and why they are `visible` only when they actually have
 * something to draw.
 */

import * as THREE from "three/webgpu";
import type { Vec3 } from "@red-opus/engine";
import { disposeTree } from "../three/dispose-tree.ts";
import { hideMissing, placeAircraft, placeTargets, setFleetPropellerEffect, swapFleetModels, type FleetState } from "./combat-fleet.ts";
import {
  BULLET_FRAMES,
  createBulletPool,
  fillBulletPool,
  MAX_BULLETS,
  type BulletInstance,
  type CameraBasis,
} from "./bullet-sprites.ts";
import { TracerPool } from "../three/tracer-pool.ts";
import { fillTracerPool } from "./tracer-bolts.ts";
import { spriteSheetPath } from "./cockpit-composition.ts";
import { renderedFovDeg } from "./cockpit-projection.ts";
import type { RoundView } from "./encounter-setup.ts";
import { CAMERA_FOV_DEG } from "./flight-view.ts";
import type { AircraftView, TargetView } from "./encounter-views.ts";

/** The baked `BULLETS.BMP` strip, sliced per bucket by UV offset/repeat so all three share one decoded image. */
const BULLETS_SHEET_WIDTH = 152;
const BULLETS_SHEET_HEIGHT = 21;

/** The baked strip's frame widths, in order — its own `manifest.json` frame table. */
const BULLETS_FRAME_WIDTHS: readonly number[] = [16, 16, 8, 8, 8, 8, 8, 8, 24, 8, 8, 24, 8];

function frameOriginX(frame: number): number {
  return BULLETS_FRAME_WIDTHS.slice(0, frame).reduce((sum, width) => sum + width, 0);
}

/**
 * One decoded copy of the strip for all three buckets — the frame window is
 * picked in the shader (`bulletFrameUv`), not by `texture.offset`/`repeat`, so
 * nothing here needs a texture per size rung.
 */
export function bulletSheetTexture(): THREE.Texture {
  const texture = new THREE.TextureLoader().load(spriteSheetPath("BULLETS.BMP"));
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestFilter;
  texture.generateMipmaps = false;
  // NoColorSpace: the strip is baked straight from the game's own palette
  // bytes, not a photograph — see `webgpu-support.ts`'s colour-management note.
  texture.colorSpace = THREE.NoColorSpace;
  return texture;
}

/** This bucket's frame window in the strip. Frames lie left to right at y=0; UV origin is bottom-left. */
function bulletFrameUv(bucket: number): THREE.Node<"vec2"> {
  const sprite = BULLET_FRAMES[bucket]!;
  const size = THREE.TSL.vec2(sprite.width / BULLETS_SHEET_WIDTH, sprite.height / BULLETS_SHEET_HEIGHT);
  const origin = THREE.TSL.vec2(
    frameOriginX(sprite.frame) / BULLETS_SHEET_WIDTH,
    1 - sprite.height / BULLETS_SHEET_HEIGHT,
  );
  return THREE.TSL.uv().mul(size).add(origin);
}

/**
 * The tracer ramp. A round's own art is a *dark* mark — a census of the eight
 * in-flight frames' texels puts the streak's body at RGB 20/255 and its
 * brightest fringe at 121/255, over a binary alpha — which is a 1990 CRT's
 * idea of a bullet against a bright sky and a modern panel's idea of nothing.
 * The sanctioned port departure (user, 2026-08-21: rounds must be *plainly
 * visible*) keeps the frame's own silhouette and its own internal shading and
 * only reassigns the greys to an incandescent ramp: the dark body becomes the
 * hot core, the light fringe the cooler edge. Frame choice, size and the belt
 * cadence stay exactly the decoded rules.
 */
const TRACER_CORE_COLOR = 0xfff4cf;
const TRACER_FRINGE_COLOR = 0xff8a1e;
/** The brightest opaque texel in the in-flight frames (121/255) — the top of the ramp. */
const BULLETS_BRIGHTEST_GREY = 121 / 255;
/** The bake writes palette index 0 as alpha 0 and everything else as 255, so any mid cut is exact. */
const BULLET_ALPHA_TEST = 0.5;

/**
 * A round's material, and the one hard rule about it: it is **opaque**.
 *
 * Measured on the real GPU (headed WebGPU, 2026-08-22), eight material
 * variants against the same instance matrices in the same frames: with
 * `transparent: true` *or* `depthWrite: false` the bullet `InstancedMesh`es
 * rasterize **zero pixels** — peak frame difference 0 at the very pixels
 * `camera.project` puts the rounds on — and with both off they draw correctly
 * at exactly those pixels. Additive-versus-normal blending makes no difference
 * either way; opting out of the depth buffer is the whole of it. That is the
 * same trap `docs/engineering/lessons.md` records for the survey grid under
 * this renderer's reversed-Z depth buffer ("`depthWrite: false` … can change
 * whether the object draws at all"), and the reason `three/screen-sprites.ts`
 * deliberately keeps every sprite pool opaque.
 *
 * So the frame's binary alpha is cut with `alphaTest` — a hard mask, which is
 * the faithful reading anyway: a 1990 blitter does not blend — and the colour
 * is a plain replacement rather than a tint through the near-black art, since
 * multiplying an incandescent colour by a 20/255 texel puts the round straight
 * back out of sight.
 */
export function bulletTracerMaterial(bucket: number, texture: THREE.Texture): THREE.MeshBasicNodeMaterial {
  const material = new THREE.MeshBasicNodeMaterial({ side: THREE.DoubleSide });
  const sheet = THREE.TSL.texture(texture, bulletFrameUv(bucket));
  const heat = THREE.TSL.mix(
    THREE.TSL.color(TRACER_CORE_COLOR),
    THREE.TSL.color(TRACER_FRINGE_COLOR),
    sheet.r.div(BULLETS_BRIGHTEST_GREY).clamp(),
  );
  material.colorNode = THREE.TSL.vec4(heat, sheet.a);
  material.alphaTest = BULLET_ALPHA_TEST;
  // A tracer is a light, not a lit surface: fogging it toward the sky is the
  // one thing that would take contrast back off the mark the player must see,
  // and at the ranges a round lives to (≤ ~700 m against a 26 km far plane)
  // there is nothing real to model there anyway.
  material.fog = false;
  return material;
}

/**
 * One `InstancedMesh` per bullet size bucket — three draw calls for every
 * round in the sky, however many are flying. Quads are oriented to face the
 * camera on the CPU (one shared quaternion, 48 matrix writes a frame), which
 * is cheaper than a billboard shader.
 *
 * No `renderOrder`: an opaque mesh cannot be ordered above the propeller disc,
 * because the disc is transparent and every transparent object draws after
 * every opaque one whatever their render orders say. The 2026-08-21 report
 * behind that flag — "rounds dimmed to invisibility under the disc" — was a
 * near-black round under a haze; an incandescent one reads straight through
 * the disc's capped alpha.
 */
function bulletMesh(bucket: number, texture: THREE.Texture): THREE.InstancedMesh {
  const geometry = new THREE.PlaneGeometry(1, 1);
  const mesh = new THREE.InstancedMesh(geometry, bulletTracerMaterial(bucket, texture), MAX_BULLETS);
  mesh.frustumCulled = false;
  mesh.count = 0;
  mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  return mesh;
}

interface VisualsState extends FleetState {
  readonly group: THREE.Group;
  readonly bullets: readonly THREE.InstancedMesh[];
  /** The one decoded `BULLETS.BMP`, shared by all three buckets; `disposeTree` frees materials, not textures. */
  readonly bulletSheet: THREE.Texture;
  readonly bulletPool: BulletInstance[];
  /** ENHANCED EFFECTS' gunfire (`three/tracer-pool.ts`); idle and drawing nothing while the toggle is off. */
  readonly tracers: TracerPool;
  /** Which of the two gunfire paths is live this frame — see the module doc's replace-never-add note. */
  enhancedTracers: boolean;
  /** World-space deltas from the player's pose origin to each *drawn* gun breech — see `gun-muzzles.ts`. */
  breechOffsets: readonly Vec3[];
  /** The same, for the sim's own barrels; `muzzleShiftFor` subtracts one from the other. */
  barrelOffsets: readonly Vec3[];
}

const bulletMatrix = new THREE.Matrix4();
const bulletPosition = new THREE.Vector3();
const bulletScale = new THREE.Vector3();
const bulletRoll = new THREE.Quaternion();
const bulletOrientation = new THREE.Quaternion();
const cameraPosition = new THREE.Vector3();
const cameraQuaternion = new THREE.Quaternion();
const cameraRight = new THREE.Vector3();
const cameraUp = new THREE.Vector3();
const cameraForward = new THREE.Vector3();
const decomposedScale = new THREE.Vector3();
/** Screen-plane roll axis: the quad's own normal, which `camera.quaternion` sends to the view axis. */
const VIEW_AXIS = new THREE.Vector3(0, 0, 1);

/**
 * The bomb sub-list the pixel pool keeps when the tracers have taken the
 * gunfire. Reused and truncated rather than rebuilt, because `rounds.filter`
 * would allocate an array every frame of every sortie for a list that is empty
 * in every dogfight — and "a burst costs no allocation" is the property the
 * pools here exist for.
 */
const bombScratch: RoundView[] = [];

function bombsOnly(rounds: readonly RoundView[]): readonly RoundView[] {
  bombScratch.length = 0;
  for (const round of rounds) if (round.bomb) bombScratch.push(round);
  return bombScratch;
}

/**
 * Places every live round as a camera-facing quad in its size bucket's
 * instanced mesh, **rolled** so the streak lies along where the round is
 * actually going on screen (`bullet-sprites.ts`'s `bulletStreak`). The quads
 * face the camera's own orientation, which is right for every view including
 * the cockpit — the camera's world transform is whatever `placeCamera` pointed
 * it at this frame, glance included.
 *
 * The camera must therefore be placed *before* this runs: reading it after the
 * fact billboards the whole burst to last frame's attitude, and to the identity
 * rotation on the very first frame.
 */
function writeBullets(state: VisualsState, rounds: readonly RoundView[], camera: THREE.Camera): void {
  camera.updateWorldMatrix(true, false);
  camera.matrixWorld.decompose(cameraPosition, cameraQuaternion, decomposedScale);
  cameraRight.set(1, 0, 0).applyQuaternion(cameraQuaternion);
  cameraUp.set(0, 1, 0).applyQuaternion(cameraQuaternion);
  // three's camera looks down its own −Z, so forward is −Z rotated into world.
  cameraForward.set(0, 0, -1).applyQuaternion(cameraQuaternion);
  const basis: CameraBasis = { position: cameraPosition, right: cameraRight, up: cameraUp, forward: cameraForward };
  // `renderedFovDeg`, not `camera.fov`: the cockpit camera carries a view
  // offset that shears its principal point onto the painted gunsight
  // (`cockpit-projection.ts`), which makes `fov` the *virtual* frame's angle.
  // Reading it raw inflates every round by the shear ratio in cockpit view.
  const fovDeg = camera instanceof THREE.PerspectiveCamera ? renderedFovDeg(camera) : CAMERA_FOV_DEG;
  // ENHANCED EFFECTS: the tracers take the gunfire and the pixel pool is left
  // the bombs, so exactly one path draws each thing. Off, `bulletsFrom` is the
  // whole list and the tracer pool is placed empty, which hides both its meshes.
  state.tracers.place(
    state.enhancedTracers
      ? fillTracerPool(state.tracers.boltPool(), rounds, state.breechOffsets, state.barrelOffsets, basis, fovDeg)
      : 0,
    cameraQuaternion,
  );
  const bulletsFrom = state.enhancedTracers ? bombsOnly(rounds) : rounds;
  const live = fillBulletPool(
    state.bulletPool,
    bulletsFrom,
    state.breechOffsets,
    state.barrelOffsets,
    basis,
    fovDeg,
  );
  const written = state.bullets.map(() => 0);

  for (let index = 0; index < live; index++) {
    const bullet = state.bulletPool[index]!;
    const mesh = state.bullets[bullet.bucket];
    if (!mesh) continue;
    bulletPosition.set(bullet.x, bullet.y, bullet.z);
    bulletScale.set(bullet.width, bullet.height, 1);
    bulletRoll.setFromAxisAngle(VIEW_AXIS, bullet.angleRad);
    bulletOrientation.copy(cameraQuaternion).multiply(bulletRoll);
    bulletMatrix.compose(bulletPosition, bulletOrientation, bulletScale);
    mesh.setMatrixAt(written[bullet.bucket]!, bulletMatrix);
    written[bullet.bucket]! += 1;
  }

  for (const [bucket, mesh] of state.bullets.entries()) {
    mesh.count = written[bucket]!;
    mesh.instanceMatrix.needsUpdate = true;
    // A bucket with nothing in it leaves the render list rather than issuing an
    // empty draw. Pixel-identical in both toggle states — an empty instanced
    // mesh has never drawn anything — and it is what makes the ENHANCED path's
    // cost honest: with the tracers on and no bombs in the air these three go
    // to zero and the two tracer meshes replace them.
    mesh.visible = mesh.count > 0;
  }
}

export interface CombatVisuals {
  /** Add this to the scene once; everything below draws inside it. */
  readonly group: THREE.Group;
  /**
   * Places every other aeroplane and every balloon/airship for this frame, and
   * turns each aeroplane's modelled propeller — which is why a frame time comes
   * in with the poses (`combat-fleet.ts`'s `placeAircraft`).
   */
  setFleet(aircraft: readonly AircraftView[], targets: readonly TargetView[], dtS: number): void;
  /**
   * This frame's gun geometry for the player: world-space offsets from their
   * pose origin to each *painted* breech and to each of the sim's own barrels
   * (`gun-muzzles.ts`). A round is drawn shifted by the difference, so passing
   * only one of the two would either leave the rounds on the centreline or
   * double the offset.
   */
  setPlayerGuns(breeches: readonly Vec3[], barrels: readonly Vec3[]): void;
  /**
   * `camera` is needed to face the bullet quads, to roll each streak onto its
   * apparent bearing, and to pick each round's size bucket by range — so it
   * must already be placed for this frame when this is called.
   */
  update(rounds: readonly RoundView[], camera: THREE.Camera): void;
  /** ENHANCED MODELS, flipped in flight: rebuilds every aeroplane in the sky from the other source (a no-op if unchanged). */
  setEnhancedModels(enabled: boolean): void;
  /**
   * ENHANCED EFFECTS, flipped in flight: which of the two gunfire paths draws
   * the rounds. A flag rather than a rebuild — both pools exist either way and
   * the one that is off is placed empty, so the switch takes effect on the very
   * next frame and costs nothing while it is off.
   */
  setEnhancedTracers(enabled: boolean): void;
  /**
   * PROPELLER EFFECT, flipped in flight. It reaches the fleet because the
   * enhanced `.glb` carries a modelled propeller of its own, and only one of the
   * two may be on screen at a time (`modelled-propeller.ts`). A visibility flag
   * on one node per aeroplane — nothing is reloaded.
   */
  setPropellerEffect(enabled: boolean): void;
  dispose(): void;
}

export function createCombatVisuals(enhancedModels = false, enhancedTracers = false, propellerEffect = false): CombatVisuals {
  const group = new THREE.Group();
  const fleet = new THREE.Group();
  const bulletSheet = bulletSheetTexture();
  const bullets = BULLET_FRAMES.map((_, bucket) => bulletMesh(bucket, bulletSheet));
  const tracers = new TracerPool();
  const state: VisualsState = {
    group,
    fleet,
    slots: new Map(),
    meshCache: new Map(),
    enhancedModels,
    propellerEffect,
    fleetGeneration: 0,
    bullets,
    bulletSheet,
    bulletPool: createBulletPool(),
    tracers,
    enhancedTracers,
    breechOffsets: [],
    barrelOffsets: [],
    disposed: false,
  };
  group.add(fleet, ...bullets, tracers.group);

  return {
    group,
    setFleet(aircraft, targets, dtS) {
      if (state.disposed) return;
      placeAircraft(state, aircraft, dtS);
      placeTargets(state, targets);
      hideMissing(state, new Set([...aircraft.map((view) => view.id), ...targets.map((view) => view.id)]));
    },
    setPlayerGuns(breeches, barrels) {
      state.breechOffsets = breeches;
      state.barrelOffsets = barrels;
    },
    update(rounds, camera) {
      writeBullets(state, rounds, camera);
    },
    setEnhancedModels(enabled) {
      swapFleetModels(state, enabled);
    },
    setEnhancedTracers(enabled) {
      state.enhancedTracers = enabled;
    },
    setPropellerEffect(enabled) {
      setFleetPropellerEffect(state, enabled);
    },
    dispose() {
      state.disposed = true;
      disposeTree(fleet);
      for (const mesh of state.bullets) disposeTree(mesh);
      state.tracers.dispose();
      state.bulletSheet.dispose();
      state.slots.clear();
      state.meshCache.clear();
    },
  };
}
